import type { Address, MemberBack, MemberFront } from '@entities/Member/types';
import { entityAccess } from '@entities/User/permissions';
import { parseUTCDate } from '@root/src/utils/formatters/dates';

/**
 * The read boundary is where legacy shapes get cleaned up: records written
 * before `emptyStringToNull` stopped recursing hold `null` in address
 * sub-fields, which the `Address` type does not admit. Dropping those keys makes
 * the value match its type before it reaches the form or the detail page.
 */
const normalizeAddress = (address: Address | null | undefined): Address | undefined => {
  if (!address) {
    return undefined;
  }
  const entries = Object.entries(address).filter(([, value]) => value !== null && value !== undefined && value !== '');
  return entries.length ? (Object.fromEntries(entries) as Address) : undefined;
};

export const memberFromBack = ({
  _id,
  accessLevel,
  memberSince,
  registrationDate,
  createdAt,
  updatedAt,
  name,
  memberType,
  memberSubtype,
  memberCategory,
  state,
  website,
  activity,
  kyc,
  ceo,
  organizationSize,
  nbEmployees,
  annualTurnover,
  shareholding,
  affiliatedEntities,
  registrationAddress,
  mailingAddress,
  hqAddress,
  placeOfOperationAddress,
  pointOfContacts,
  publications,
  additionalInfo,
}: MemberBack): MemberFront => {
  const accessPermissions = entityAccess(accessLevel);
  return {
    name,
    memberType,
    memberSubtype,
    memberCategory,
    state,
    website,
    activity,
    kyc,
    ceo,
    organizationSize,
    nbEmployees,
    annualTurnover,
    shareholding,
    affiliatedEntities,
    registrationAddress: normalizeAddress(registrationAddress),
    mailingAddress: normalizeAddress(mailingAddress),
    hqAddress: normalizeAddress(hqAddress),
    placeOfOperationAddress: normalizeAddress(placeOfOperationAddress),
    pointOfContacts,
    publications,
    additionalInfo,
    memberSince: memberSince && memberSince.trim() ? parseUTCDate(memberSince) : undefined,
    registrationDate: registrationDate && registrationDate.trim() ? parseUTCDate(registrationDate) : undefined,
    createdAt: createdAt && createdAt.trim() ? parseUTCDate(createdAt) : undefined,
    updatedAt: updatedAt && updatedAt.trim() ? parseUTCDate(updatedAt) : undefined,
    canEdit: accessPermissions.canEdit,
    canDelete: accessPermissions.canDelete,
    id: _id,
  };
};
